import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProfileState } from '@/shared/hooks/useProfileState';
import { useEntitlements } from '@/shared/hooks/useEntitlements';
import { useAdminStatus } from '@/shared/hooks/useAdminStatus';
import { usePresence } from '@/shared/hooks/usePresence';
import { useAuthBootstrap } from '@/features/auth/hooks/useAuthBootstrap';
import { useDeviceTrust } from '@/features/auth/hooks/useDeviceTrust';
import { useAuthHydration } from '@/features/auth/hooks/useAuthHydration';
import { performSignOut } from '@/features/auth/services/authSignOut';
import { handleSignedOutTransition } from '@/features/auth/services/authTransitions';
import { captureEntryDeepLinkIfUnauthed, consumePendingDeepLink } from '@/lib/pendingDeepLink';
import type { AuthContextType } from '@/features/auth/types';

// Pin AuthContext across HMR reloads so consumers and provider always share
// the same context object. Without this, editing AuthContext.tsx in dev causes
// `useContext(AuthContext)` to return undefined for in-flight renders.
const __AUTH_CTX_KEY = '__whisprai_auth_context__';
const AuthContext: React.Context<AuthContextType | undefined> =
  (globalThis as any)[__AUTH_CTX_KEY] ??
  ((globalThis as any)[__AUTH_CTX_KEY] = createContext<AuthContextType | undefined>(undefined));

// Capture the entry URL once at module load (before React renders), so a deep link
// can be resumed after the user authenticates.
if (typeof window !== 'undefined') {
  captureEntryDeepLinkIfUnauthed();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [privateKey, setPrivateKey] = useState<CryptoKey | null>(null);

  const { profile, fetchProfile, resetProfile } = useProfileState();
  const { credits, allocatedCredits, creditsLoaded, isPremium, fetchCredits, reset: resetEntitlements } = useEntitlements();
  const { isAdmin, fetchAdminStatus, reset: resetAdmin } = useAdminStatus();
  const { isDeviceTrusted, evaluateAndSet, refreshDeviceTrust, reset: resetDeviceTrust } = useDeviceTrust();

  const resetAll = useCallback(async () => {
    await handleSignedOutTransition({
      resetProfile,
      resetAdmin,
      resetDeviceTrust,
      resetEntitlements,
      setPrivateKey,
    });
  }, [resetProfile, resetAdmin, resetDeviceTrust, resetEntitlements]);

  const { hydrateUser } = useAuthHydration({
    fetchProfile,
    fetchCredits,
    fetchAdminStatus,
    setPrivateKey,
    evaluateDeviceTrust: evaluateAndSet,
    setIsAuthReady: (ready: boolean) => {
      setIsAuthReadyExt(ready);
    },
  });

  // We need a way to set isAuthReady from hydration
  const [isAuthReadyExt, setIsAuthReadyExt] = useState<boolean | null>(null);

  const { session, user, loading, isAuthReady: bootstrapAuthReady, setIsAuthReady } = useAuthBootstrap({
    onHydrate: hydrateUser,
    onSignedOut: resetAll,
  });

  // Sync external isAuthReady from hydration
  useEffect(() => {
    if (isAuthReadyExt !== null) {
      setIsAuthReady(isAuthReadyExt);
      setIsAuthReadyExt(null);
    }
  }, [isAuthReadyExt, setIsAuthReady]);

  const isAuthReady = bootstrapAuthReady;

  usePresence(user);

  // Resume pending deep link after sign-in.
  useEffect(() => {
    if (!user || !isAuthReady) return;
    // Skip resume on /auth — Auth.tsx owns that redirect (honors ?redirect= and
    // getPostAuthDestination). Running both causes a race where the stashed
    // marketing path overrides Auth's Navigate, bouncing the user back.
    if (window.location.pathname.startsWith('/auth')) return;
    const pending = consumePendingDeepLink();
    if (pending && pending !== window.location.pathname + window.location.search) {
      // Use replace to avoid adding /auth to history.
      window.history.replaceState({}, '', pending);
      // Trigger SPA route handling.
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [user, isAuthReady]);

  // Refresh admin status on focus/visibility
  useEffect(() => {
    if (!user) return;
    const refreshAdmin = () => fetchAdminStatus(user.id);
    refreshAdmin();
    window.addEventListener('focus', refreshAdmin);
    const handleVisibilityChange = () => { if (!document.hidden) refreshAdmin(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshAdmin);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchAdminStatus]);

  const signUp = async (email: string, password: string, displayName: string, referralCode?: string) => {
    const metadata: Record<string, string> = { display_name: displayName };
    if (referralCode) metadata.referral_code = referralCode;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin, data: metadata },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await performSignOut(user?.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const refreshCredits = async () => {
    if (user) await fetchCredits(user.id);
  };

  const handleRefreshDeviceTrust = async () => {
    if (user) await refreshDeviceTrust(user.id);
  };

  return (
    <AuthContext.Provider value={{
      session, user, profile, loading, isAuthReady, isAdmin, privateKey,
      credits, allocatedCredits, creditsLoaded, isPremium,
      isDeviceTrusted, refreshDeviceTrust: handleRefreshDeviceTrust,
      signUp, signIn, signOut, refreshProfile, refreshCredits,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

let __useAuthWarned = false;
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    if (import.meta.env.DEV) {
      if (!__useAuthWarned) {
        __useAuthWarned = true;
        // eslint-disable-next-line no-console
        console.warn('[useAuth] context missing — likely HMR transient. Returning empty stub.');
      }
      return {} as AuthContextType;
    }
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
